import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MessageMedia } from "@/components/chat/MessageMedia";
import { PageHeader, StatusPill, Empty } from "./ui";
import { errMsg } from "@/lib/api";
import { fmtDateTime, maskPhone } from "@/lib/format";

const FILTERS = [["", "Todos"], ["pendente", "A conferir"], ["conferido", "Conferidos"], ["divergente", "Divergentes"]];

export default function Receipts() {
  const { api } = useOutletContext();
  const [status, setStatus] = useState("pendente");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/receipts", { params: { status: status || undefined } }).then((r) => setRows(r.data)).catch(() => setRows([])), [api, status]);
  useEffect(() => { load(); }, [load]);
  const mark = async (id, receipt_status) => {
    try { await api.patch(`/admin/messages/${id}`, { receipt_status }); toast.success("Comprovante atualizado."); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Comprovantes Pix" description="Confira cada comprovante no extrato bancário antes de marcar como conferido.">
        {FILTERS.map(([v, l]) => (
          <button key={v || "all"} data-testid={`receipts-filter-${v || "all"}`} onClick={() => setStatus(v)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${status === v ? "bg-zap-deep text-white" : "bg-zap-mist text-zap-ink/75 hover:bg-zap-line"}`}>{l}</button>
        ))}
      </PageHeader>
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {rows === null ? <Empty>Carregando…</Empty> : rows.length === 0 ? <Empty testId="receipts-empty">Nenhum comprovante nesta situação.</Empty> : (
          <ul className="divide-y divide-zap-line bg-white">
            {rows.map((m) => (
              <li key={m.id} data-testid={`receipt-row-${m.id}`} className="grid gap-4 px-5 py-4 md:px-8 xl:grid-cols-[280px_minmax(0,1fr)_auto]">
                <MessageMedia msg={m} client={api} base="/admin/files" />
                <div className="min-w-0 space-y-1 text-[15px]">
                  <p className="font-semibold">{m.participant?.name || "Participante excluído"}</p>
                  {m.participant && <p className="text-sm text-muted-foreground">{maskPhone(m.participant.whatsapp)} · {m.participant.city}/{m.participant.state}</p>}
                  <p className="text-sm">Recebido em {fmtDateTime(m.created_at)}</p>
                  {m.kind === "image" && m.receipt_ai && (
                    <p data-testid={`receipt-ai-${m.id}`} className="text-sm font-medium text-[#7A5A14]">
                      {m.receipt_ai.answer === "sim" ? "Identificado automaticamente na fotografia" : "Possível comprovante — conferir"}{m.receipt_ai.reason ? `: ${m.receipt_ai.reason}` : ""}
                    </p>
                  )}
                  {m.text && <p className="text-sm italic">“{m.text}”</p>}
                  <div className="flex items-center gap-2 pt-1"><StatusPill status={m.receipt_status} />{m.receipt_checked_by && <span className="text-xs text-muted-foreground">por {m.receipt_checked_by}</span>}</div>
                  {m.participant && <Link to={`/admin/conversas?p=${m.participant_id}`} className="text-sm font-semibold text-zap-green hover:underline">Abrir conversa</Link>}
                </div>
                <div className="flex flex-wrap gap-2 xl:flex-col">
                  <Button data-testid={`receipt-ok-${m.id}`} size="sm" disabled={m.receipt_status === "conferido"} onClick={() => mark(m.id, "conferido")}>Conferido</Button>
                  <Button data-testid={`receipt-bad-${m.id}`} size="sm" variant="outline" disabled={m.receipt_status === "divergente"} onClick={() => mark(m.id, "divergente")}>Divergente</Button>
                  {m.receipt_status !== "pendente" && <Button data-testid={`receipt-reset-${m.id}`} size="sm" variant="ghost" onClick={() => mark(m.id, "pendente")}>Voltar a conferir</Button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
